import type { ReactNode } from 'react';

import LucideSymbolIcon from '../../icons/LucideSymbolIcon';

function joinClasses(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(' ');
}

export function AcademicPanelCard({
  icon,
  title,
  subtitle,
  badge,
  children,
  footer,
  variant = 'default',
  flush,
  id,
}: {
  icon: ReactNode | string;
  title: string;
  subtitle?: ReactNode;
  badge?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  variant?: 'default' | 'accent' | 'preview' | 'success';
  /** Corps sans padding (tableaux pleine largeur). */
  flush?: boolean;
  id?: string;
}) {
  return (
    <section
      id={id}
      className={joinClasses('academic-panel-card', `academic-panel-card--${variant}`, id && 'scroll-mt-24')}
    >
      <header className="academic-panel-card__header">
        <span className="academic-panel-card__icon" aria-hidden>
          {typeof icon === 'string' ? <LucideSymbolIcon name={icon} tone="primary" size={20} /> : icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="academic-panel-card__title">{title}</h3>
          {subtitle ? <p className="academic-panel-card__subtitle">{subtitle}</p> : null}
        </div>
        {badge ? <div className="shrink-0">{badge}</div> : null}
      </header>
      <div className={flush ? undefined : 'academic-panel-card__body'}>{children}</div>
      {footer ? <footer className="academic-panel-card__footer">{footer}</footer> : null}
    </section>
  );
}

export function AcademicPanelTableWrap({
  children,
  empty,
  footer,
}: {
  children: ReactNode;
  empty?: ReactNode;
  /** Barre de pagination ou actions sous le tableau (hors zone scroll horizontal). */
  footer?: ReactNode;
}) {
  if (empty) {
    return <div className="academic-panel-empty">{empty}</div>;
  }
  return (
    <>
      <div className="academic-panel-table-wrap">{children}</div>
      {footer ? <div className="academic-panel-table-footer">{footer}</div> : null}
    </>
  );
}

export function AcademicPanelStatBanner({
  children,
  tone = 'success',
}: {
  children: ReactNode;
  tone?: 'success' | 'info';
}) {
  return (
    <p
      className={joinClasses(
        'academic-panel-stat-banner',
        tone === 'info' && 'academic-panel-stat-banner--info',
      )}
    >
      {children}
    </p>
  );
}

export function AcademicPanelListItem({ children }: { children: ReactNode }) {
  return <li className="academic-panel-list-item">{children}</li>;
}
